import React, { useMemo, useCallback, useState } from 'react';
import { Menu } from 'antd';
import { SearchOutlined } from '@ant-design/icons';

import PropTypes from 'prop-types';
import OnTraccrCheckbox from './inputs/OnTraccrCheckbox';
import OnTraccrTextInput from './inputs/OnTraccrTextInput';
import { includesTerm } from '../helpers/helpers';
import Colors from '../constants/Colors';

function flattenNodes(nodes) {
  return nodes.reduce(
    (acc, n) => acc.concat(n, flattenNodes(n.children || [])),
    [],
  );
}

export default function FilterNodeRedesign({
  node,
  activeSet,
  onFilterChange,
  category,
  style,
}) {
  const [searchValue, setSearchValue] = useState('');

  const descendantKeys = useMemo(
    () => flattenNodes([node]).map((n) => n.key),
    [node],
  );

  const hasChildren = node.children?.length > 0;

  const selectedCount = descendantKeys.filter((k) => activeSet.has(k)).length;
  const checked = (
    (!hasChildren && activeSet.has(node.key))
    || (hasChildren && selectedCount === (descendantKeys.length - 1))
  );

  const indeterminate = hasChildren
   && selectedCount > 0
   && selectedCount < (descendantKeys.length - 1);

  const handleChange = useCallback(
    (nextChecked) => {
      const next = new Set(activeSet);
      if (node.children?.length > 0) {
        if (nextChecked) descendantKeys.forEach((k) => next.add(k));
        else descendantKeys.forEach((k) => next.delete(k));
      } else if (nextChecked) {
        next.add(node.key);
      } else {
        next.delete(node.key);
      }
      onFilterChange(Array.from(next), category);
    },
    [activeSet, descendantKeys, node, onFilterChange, category],
  );

  const relevantChildren = useMemo(() => {
    if (!searchValue || !node.children) return node.children;
    return node.children.filter((child) => includesTerm(child.title, searchValue));
  }, [node.children, searchValue]);

  const getStyle = (index) => {
    if (relevantChildren && index === relevantChildren.length - 1) return { paddingBottom: 10 };
    return {
      paddingBottom: 5,
    };
  };

  const hasAll = useMemo(() => !searchValue || includesTerm('all', searchValue), [searchValue]);

  if (hasChildren) {
    return (
      <>
        <OnTraccrTextInput
          prefix={<SearchOutlined style={{ color: Colors.GRAY_700, padding: '0px 5px 3px 0px' }} />}
          placeholder="Search"
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          style={{ margin: 6, width: '95%', marginBottom: 10 }}
        />
        { hasAll && (
          <Menu.Item
            key={node.key}
            onItemHover={() => {}}
            onClick={() => {}}
            style={{ padding: '5px 12px' }}
          >
            <OnTraccrCheckbox
              label="All"
              value={checked}
              onChange={handleChange}
              indeterminate={indeterminate}
            />
          </Menu.Item>
        )}
        {relevantChildren.map((child, index) => (
          <FilterNodeRedesign
            key={child.key}
            node={child}
            activeSet={activeSet}
            onFilterChange={onFilterChange}
            category={category}
            style={getStyle(index)}
          />
        ))}
      </>
    );
  }

  return (
    <Menu.Item
      key={node.key}
      title={node.title}
      onItemHover={() => {}}
      onClick={() => {}}
      style={{ padding: '5px 12px', ...style }}
    >
      <OnTraccrCheckbox
        label={node.title}
        value={checked}
        onChange={handleChange}
      />
    </Menu.Item>
  );
}

FilterNodeRedesign.propTypes = {
  node: PropTypes.shape({
    title: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
    children: PropTypes.array,
  }).isRequired,
  activeSet: PropTypes.instanceOf(Set).isRequired,
  onFilterChange: PropTypes.func.isRequired,
  category: PropTypes.string.isRequired,
  style: PropTypes.object,
};

FilterNodeRedesign.defaultProps = {
  style: {},
};
